import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";

import { Awesome, DevModal } from "@components/Helpers";
import { url_reverse } from "@components/Utils";

const PROJECTS = [
    { value: "store", label: "Store" },
    { value: "market", label: "Market" },
    { value: "cloud", label: "Cloud" },
];

const ALL_PROJECTS = [...PROJECTS, { value: "all", label: "All" }];

const MerchantGame = ({ game_id }) => {
    if (game_id === null) {
        return <span style={{ backgroundColor: "lightgreen" }}>many</span>;
    }
    return <Link to={url_reverse("games:page", { game_id })}>{game_id}</Link>;
};

const MerchantRow = ({ merchantKey, merchantData, onEdit, onToggle }) => {
    const isActive = merchantData.is_disabled === false;
    return (
        <tr>
            <td>{merchantKey}</td>
            <td>{merchantData.email}</td>
            <td>
                <MerchantGame game_id={merchantData.game_id} />
            </td>
            <td>{merchantData.project}</td>
            <td>{merchantData.spp}</td>
            <td>{isActive ? "Да" : "Нет"}</td>
            <td>
                <div className="d-flex gap-3 justify-content-center">
                    <Button
                        variant="outline-primary"
                        size="md"
                        onClick={() => onEdit(merchantKey, merchantData)}
                        title="Редактировать"
                        style={{ minWidth: "40px", height: "38px" }}
                    >
                        <Awesome type="pencil" />
                    </Button>
                    <div style={{ padding: "6px" }} />
                    <Button
                        variant={isActive ? "outline-warning" : "outline-success"}
                        size="md"
                        onClick={() => onToggle(merchantKey, merchantData)}
                        title={isActive ? "Отключить" : "Включить"}
                        style={{ minWidth: "40px", height: "38px" }}
                    >
                        <Awesome type={isActive ? "ban" : "check"} />
                    </Button>
                </div>
            </td>
        </tr>
    );
};

const MerchantFormModal = ({ show, onHide, merchant, isEdit, onSave }) => {
    const [formData, setFormData] = useState({
        id: "",
        name: "",
        game_id: "",
        project: "store",
        spp: "",
        enabled: true,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (show) {
            if (isEdit && merchant) {
                setFormData({
                    id: merchant.id || "",
                    name: merchant.email || "",
                    game_id: merchant.game_id || "",
                    project: merchant.project || "store",
                    spp: merchant.spp || "",
                    enabled: !merchant.is_disabled,
                });
            } else {
                setFormData({
                    id: "",
                    name: "",
                    game_id: "",
                    project: "store",
                    spp: "",
                    enabled: true,
                });
            }
            setErrors({});
        }
    }, [show, merchant, isEdit]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.id) newErrors.id = "ID обязателен";
        if (!formData.name) newErrors.name = "Название обязательно";
        if (!formData.project) newErrors.project = "Проект обязателен";

        if (formData.id && !/^\d+$/.test(formData.id)) {
            newErrors.id = "ID должен быть числом";
        }

        if (formData.game_id && !/^\d+$/.test(formData.game_id)) {
            newErrors.game_id = "Game ID должен быть числом";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const submitData = new FormData();

            if (isEdit) {
                submitData.append("merchant_id", formData.id);
            } else {
                submitData.append("id", formData.id);
            }

            submitData.append("name", formData.name);
            submitData.append("project", formData.project);
            submitData.append("spp", formData.spp);
            submitData.append("enabled", formData.enabled ? "1" : "0");

            if (formData.game_id) {
                submitData.append("game_id", formData.game_id);
            }

            const endpoint = isEdit
                ? url_reverse("api:finstatements:merchant:update")
                : url_reverse("api:finstatements:merchant:create");

            const response = await fetch(endpoint, {
                method: "POST",
                body: submitData,
            });

            const result = await response.json();

            if (result.status === "ok") {
                onSave();
                onHide();
            } else {
                alert(`Ошибка: ${result.message || "Неизвестная ошибка"}`);
            }
        } catch (error) {
            console.error("Error submitting merchant:", error);
            alert("Ошибка при отправке данных");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <DevModal
            size="lg"
            show={show}
            onHide={onHide}
            header={isEdit ? "Редактировать мерчанта" : "Добавить мерчанта"}
            body={
                <>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>ID *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.id}
                                    onChange={(e) => handleChange("id", e.target.value)}
                                    isInvalid={Boolean(errors.id)}
                                    disabled={isEdit}
                                />
                                <Form.Control.Feedback type="invalid">{errors.id}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Название *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    isInvalid={Boolean(errors.name)}
                                    placeholder="@system.money.mail.ru"
                                />
                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Game ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.game_id}
                                    onChange={(e) => handleChange("game_id", e.target.value)}
                                    isInvalid={Boolean(errors.game_id)}
                                    placeholder="Оставьте пустым для many"
                                />
                                <Form.Control.Feedback type="invalid">{errors.game_id}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Проект *</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={formData.project}
                                    onChange={(e) => handleChange("project", e.target.value)}
                                    isInvalid={Boolean(errors.project)}
                                >
                                    <option value="store">Store</option>
                                    <option value="market">Market</option>
                                    <option value="cloud">Cloud</option>
                                    <option value="all">All</option>
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">{errors.project}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>СПП</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.spp}
                                    onChange={(e) => handleChange("spp", e.target.value)}
                                    placeholder={"VKPL001"}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Активен"
                                    checked={formData.enabled}
                                    onChange={(e) => handleChange("enabled", e.target.checked)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </>
            }
            footer={
                <>
                    <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                        Отмена
                    </Button>
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Сохранение...
                            </>
                        ) : isEdit ? (
                            "Сохранить"
                        ) : (
                            "Создать"
                        )}
                    </Button>
                </>
            }
            renderWrapper={(children) => <Form onSubmit={handleSubmit}>{children}</Form>}
        />
    );
};

const MerchantToggleModal = ({ show, onHide, merchant, onToggle }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isActive = merchant ? merchant.is_disabled === false : true;

    const handleToggle = async () => {
        if (!merchant) return;

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append("merchant_id", merchant.id);
            formData.append("enabled", isActive ? "0" : "1");

            const response = await fetch(url_reverse("api:finstatements:merchant:update"), {
                method: "POST",
                body: formData,
            });

            const result = await response.json();

            if (result.status === "ok") {
                onToggle();
                onHide();
            } else {
                alert(`Ошибка: ${result.message || "Неизвестная ошибка"}`);
            }
        } catch (error) {
            console.error("Error toggling merchant:", error);
            alert("Ошибка при изменении статуса мерчанта");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <DevModal
            show={show}
            onHide={onHide}
            header={isActive ? "Отключить мерчанта" : "Включить мерчанта"}
            body={
                <>
                    <p>
                        Вы уверены, что хотите {isActive ? "отключить" : "включить"} мерчанта{" "}
                        <strong>{merchant?.email}</strong> (ID: {merchant?.id})?
                    </p>
                    {isActive ? (
                        <p className="text-warning">
                            <strong>Внимание!</strong> После отключения все формируемые отчеты (в том числе за
                            предыдущие периоды) не будут содержать данные по транзакциям для этого мерчанта.
                        </p>
                    ) : (
                        <p className="text-success">
                            После включения данные по транзакциям для этого мерчанта снова будут включены в формируемые
                            отчеты.
                        </p>
                    )}
                </>
            }
            footer={
                <>
                    <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                        Отмена
                    </Button>
                    <Button variant={isActive ? "warning" : "success"} onClick={handleToggle} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                {isActive ? "Отключение..." : "Включение..."}
                            </>
                        ) : isActive ? (
                            "Отключить"
                        ) : (
                            "Включить"
                        )}
                    </Button>
                </>
            }
        />
    );
};

export default function Merchants() {
    const [merchantsData, setMerchantsData] = useState(null);
    const [merchantsFilters, setMerchantsFilters] = useState({
        id: "",
        email: "",
        game_id: "",
        project: "",
        spp: "",
        is_disabled: "",
    });

    const [showMerchantForm, setShowMerchantForm] = useState(false);
    const [showMerchantToggle, setShowMerchantToggle] = useState(false);
    const [selectedMerchant, setSelectedMerchant] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const fetchMerchantsData = async () => {
        try {
            const response = await fetch(url_reverse("api:finstatements:merchant:list"));
            const data = await response.json();
            setMerchantsData(data);
        } catch (error) {
            console.error("Error fetching merchants:", error);
        }
    };

    useEffect(() => {
        fetchMerchantsData();
    }, []);

    const handleMerchantAdd = () => {
        setSelectedMerchant(null);
        setIsEditMode(false);
        setShowMerchantForm(true);
    };

    const handleMerchantEdit = (merchantKey, merchantData) => {
        setSelectedMerchant({
            id: merchantKey,
            email: merchantData.email,
            game_id: merchantData.game_id,
            project: merchantData.project,
            spp: merchantData.spp,
            is_disabled: merchantData.is_disabled,
        });
        setIsEditMode(true);
        setShowMerchantForm(true);
    };

    const handleMerchantToggle = (merchantKey, merchantData) => {
        setSelectedMerchant({
            id: merchantKey,
            email: merchantData.email,
            game_id: merchantData.game_id,
            project: merchantData.project,
            spp: merchantData.spp,
            is_disabled: merchantData.is_disabled,
        });
        setShowMerchantToggle(true);
    };

    const handleMerchantFilterChange = (field, value) => {
        setMerchantsFilters((prev) => ({ ...prev, [field]: value }));
    };

    const filteredMerchants = useMemo(() => {
        if (!merchantsData || !merchantsData.merchants) return [];
        return Object.entries(merchantsData.merchants).filter(([merchantKey, merchantData]) => {
            return (
                (merchantsFilters.id === "" || merchantKey.toString().includes(merchantsFilters.id)) &&
                (merchantsFilters.email === "" ||
                    (merchantData.email || "").toLowerCase().includes(merchantsFilters.email.toLowerCase())) &&
                (merchantsFilters.game_id === "" ||
                    (merchantData.game_id || "").toString().includes(merchantsFilters.game_id)) &&
                (merchantsFilters.project === "" ||
                    (merchantData.project || "").toLowerCase().includes(merchantsFilters.project.toLowerCase())) &&
                (merchantsFilters.spp === "" || (merchantData.spp || "").toString().includes(merchantsFilters.spp)) &&
                (merchantsFilters.is_disabled === "" ||
                    (merchantData.is_disabled === false ? "Да" : "Нет")
                        .toLowerCase()
                        .includes(merchantsFilters.is_disabled.toLowerCase()))
            );
        });
    }, [merchantsData, merchantsFilters]);

    const merchantsTableContent = useMemo(() => {
        return filteredMerchants.map(([merchantKey, merchantData]) => (
            <MerchantRow
                key={merchantKey}
                merchantKey={merchantKey}
                merchantData={merchantData}
                onEdit={handleMerchantEdit}
                onToggle={handleMerchantToggle}
            />
        ));
    }, [filteredMerchants]);

    return (
        <>
            <Card>
                <Card.Header>Список мерчантов</Card.Header>
                <Card.Body>
                    <div className="mb-3 d-flex justify-content-end">
                        <Button variant="success" onClick={handleMerchantAdd}>
                            + добавить
                        </Button>
                    </div>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: "100px" }}>ID</th>
                                <th>Название</th>
                                <th style={{ width: "100px" }}>Игра</th>
                                <th style={{ width: "120px" }}>Проект</th>
                                <th style={{ width: "90px" }}>СПП</th>
                                <th style={{ width: "80px" }}>Активен</th>
                                <th style={{ width: "140px", textAlign: "center" }}>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <Form.Control
                                        size="sm"
                                        value={merchantsFilters.id}
                                        onChange={(e) => handleMerchantFilterChange("id", e.target.value)}
                                        placeholder="ИД"
                                    />
                                </td>
                                <td>
                                    <Form.Control
                                        size="sm"
                                        value={merchantsFilters.email}
                                        onChange={(e) => handleMerchantFilterChange("email", e.target.value)}
                                        placeholder="Название мерчанта"
                                    />
                                </td>
                                <td>
                                    <Form.Control
                                        size="sm"
                                        value={merchantsFilters.game_id}
                                        onChange={(e) => handleMerchantFilterChange("game_id", e.target.value)}
                                        placeholder="ИД игры из кабинета"
                                    />
                                </td>
                                <td>
                                    <Form.Control
                                        as="select"
                                        value={merchantsFilters.project}
                                        size="sm"
                                        onChange={(e) => handleMerchantFilterChange("project", e.target.value)}
                                    >
                                        <option value=""></option>
                                        {ALL_PROJECTS.map((p) => (
                                            <option key={p.value} value={p.value}>
                                                {p.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </td>
                                <td>
                                    <Form.Control
                                        size="sm"
                                        value={merchantsFilters.spp}
                                        onChange={(e) => handleMerchantFilterChange("spp", e.target.value)}
                                        placeholder="СПП"
                                    />
                                </td>
                                <td>
                                    <Form.Control
                                        size="sm"
                                        value={merchantsFilters.is_disabled}
                                        onChange={(e) => handleMerchantFilterChange("is_disabled", e.target.value)}
                                        placeholder="Да/Нет"
                                    />
                                </td>
                                <td></td>
                            </tr>
                            {merchantsTableContent}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
            <MerchantFormModal
                show={showMerchantForm}
                onHide={() => setShowMerchantForm(false)}
                merchant={selectedMerchant}
                isEdit={isEditMode}
                onSave={fetchMerchantsData}
            />
            <MerchantToggleModal
                show={showMerchantToggle}
                onHide={() => setShowMerchantToggle(false)}
                merchant={selectedMerchant}
                onToggle={fetchMerchantsData}
            />
        </>
    );
}
